<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useSessionStore } from "@/stores/session";
import { OAUTH_AUTHORIZE_ROUTE } from "@/nav/shellRoutes";
import Login from "./Login.vue";
import { oauthQuery, resumeUrl } from "../oauthFlow";

/** How long a held session is given to resume before the sign-in is drawn. */
const SETTLE_MS = 2500;

/**
 * 🔴 **SIGN IN, FOR AN MCP CLIENT** (D133). Devin: *"I should be able to just click my Google login or
 * my passkey and that should authenticate the MCP."* The authorization server sent the browser here
 * with a signed query. If the person is not signed in, this is the Desk's own sign-in — Google,
 * passkey, password — with Google told to bring the browser back here. The moment a session exists,
 * the browser is sent to the server's authorize endpoint with the same signed query, and the server
 * takes it from there (consent, then back to the client with a code).
 */
export default defineComponent({
  name: "OAuthAuthorize",
  components: { Login },
  setup() {
    const session = useSessionStore();
    const query = oauthQuery();

    const resume = (): void => {
      if (query === "") return;
      window.location.replace(resumeUrl(query));
    };
    onMounted(() => {
      if (session.status === "ready") resume();
    });
    watch(
      () => session.status,
      (status) => {
        if (status === "ready") resume();
      },
    );
    /** The store starts `signed-out` and resumes the held session asynchronously; the sign-in is drawn only once that has settled. */
    const settling = computed(() => session.status === "resolving" || (session.status === "signed-out" && !settled.value));
    const settled = ref(false);
    onMounted(() => {
      window.setTimeout(() => (settled.value = true), SETTLE_MS);
    });
    return { session, query, settling, callbackPath: OAUTH_AUTHORIZE_ROUTE };
  },
});
</script>

<template>
  <div data-testid="oauth-authorize">
    <p v-if="query === ''" class="oauth-flow__missing" data-testid="oauth-missing">
      Nothing to sign in to. Start again from the app that asked you to connect to Pinksy.
    </p>
    <p v-else-if="session.status === 'ready' || settling" class="oauth-flow__missing" data-testid="oauth-resuming">{{ session.status === "ready" ? "Signed in — connecting…" : "One moment…" }}</p>
    <Login v-else :google-callback-path="callbackPath" />
  </div>
</template>

<style scoped>
.oauth-flow__missing {
  margin: 20vh auto 0;
  max-width: 480px;
  text-align: center;
  padding: 0 16px;
}
</style>
